// The orb's bubble in WebGL — 16 Sep 2026 (orb.ts has the why and the motion; this paints a frame).
//
// PIT'S KIND OF GLASS, IN kadmon'S COLOURS. For every pixel the fragment shader finds the rim at that
// angle (the same sum as `bubbleRadius` in orb.ts — keep the two in step) and paints the bubble:
//   - a pearl body, lit from up and to the left, under a soap film coloured over the whole surface:
//     bands of the chosen accent, its bright, pale tint, pearl and deep (never the rainbow),
//     richest where the glass is seen edge on and on one side more than the other, faint over the
//     middle, with one soft seam where the film thins — flowing in or out with `flow`;
//   - a soft window of light up and to the left;
//   - streaks of light hugging the rim on the lit side, and a crisp edge;
//   - the funnel's radar: a sweep of light that turns through the glass with `swirl`, as bright as
//     `sweep` says — full while she thinks;
//   - outside, a faint edge of the accent, gone before the canvas's edge (T392a: the light look has
//     no glow, so it is a quarter of what it was).
// On a light surface the film is a touch fainter, carries the pale tint where it would carry pearl,
// and its edge is the deep accent, so the bubble still reads on white.
//
// WHERE THIS CANNOT RUN — no WebGL, a shader the device refuses, a lost context — `createOrbGl`
// answers null or `onLost` fires, and the caller paints `drawOrb2d` on a fresh canvas.
import {
  ORB_IMPULSES,
  ORB_RADIUS,
  onDarkSurface,
  orbHues,
  rgbOf,
  type OrbFrame,
  type OrbPalette,
} from './orb.ts';

const VERTEX = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec2 vUv;
uniform float uSide;
uniform float uScale;
uniform float uWobble;
uniform float uTime;
uniform float uFlow;
uniform float uSwirl;
uniform float uGlow;
uniform float uLight;
uniform float uSweep;
uniform float uDim;
uniform float uOnDark;
uniform vec3 uAccent;
uniform vec3 uBright;
uniform vec3 uDeep;
uniform vec3 uTint;
uniform vec3 uPearl;
uniform vec3 uShade;
uniform vec3 uGrey;
uniform vec3 uImpulses[${ORB_IMPULSES}];

const float TAU = 6.28318530718;
const float RADIUS = ${ORB_RADIUS.toFixed(4)};

// 2D simplex noise — Ian McEwan and Stefan Gustavson, Ashima Arts (MIT licence).
vec3 permute(vec3 x) { return mod(((x * 34.0) + 1.0) * x, 289.0); }
float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod(i, 289.0);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

float rimRadius(float a) {
  float r = 1.0 + uWobble * (0.55 * sin(3.0 * a + uTime * 0.9)
    + 0.35 * sin(2.0 * a - uTime * 1.3 + 1.7)
    + 0.25 * sin(5.0 * a + uTime * 1.7 + 0.6));
  for (int i = 0; i < ${ORB_IMPULSES}; i++) {
    vec3 im = uImpulses[i];
    if (im.z > 0.0) {
      float d = atan(sin(a - im.x), cos(a - im.x));
      r += im.y * exp(-(d * d) / (im.z * im.z));
    }
  }
  return r * uScale * RADIUS;
}

// A thin film's colours, from the accent: accent, bright, pearl, tint, deep, and round again.
vec3 film(float t) {
  vec3 pale = mix(uTint, uPearl, uOnDark);
  float k = fract(t) * 5.0;
  float f = smoothstep(0.0, 1.0, fract(k));
  if (k < 1.0) return mix(uAccent, uBright, f);
  if (k < 2.0) return mix(uBright, pale, f);
  if (k < 3.0) return mix(pale, uTint, f);
  if (k < 4.0) return mix(uTint, uDeep, f);
  return mix(uDeep, uAccent, f);
}

void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float len = length(p);
  float a = atan(p.y, p.x);
  float rim = rimRadius(a);
  float px = 2.0 / uSide;
  float inside = 1.0 - smoothstep(-px, px, len - rim);

  vec2 q = p / rim;
  float ql = min(length(q), 1.0);
  vec2 around = vec2(cos(a), sin(a));

  // How rich the film is round the bubble: one side more than the other, turning slowly.
  float thick = 0.5 + 0.5 * snoise(around * 0.6 + vec2(uTime * 0.1 + uSwirl * 0.25, 2.7));

  // A soap film's colour over the whole surface — no ring round an empty middle (Natanel, 17 Sep
  // 2026: first a dark hole, then a white one). The bands swirl across the bubble, drawn in towards
  // the middle while she listens and streaming out while she speaks.
  vec2 w = q + 0.26 * vec2(
    snoise(q * 0.8 + vec2(uTime * 0.07, 1.3)),
    snoise(q * 0.8 + vec2(-4.1, uTime * 0.06))
  );
  float phase = length(w) * 0.95 - uFlow * 0.75
    + snoise(w * 0.8 + vec2(uTime * 0.05, 2.0)) * 0.24 + a / TAU + thick * 0.4;
  vec3 filmColour = film(phase) * (0.95 + 0.2 * uLight);

  // The body under it: pearl, lit from up and to the left, a little deeper on the far side.
  float z = sqrt(max(0.0, 1.0 - ql * ql));
  vec3 normal = normalize(vec3(q, z + 0.001));
  float lit = max(dot(normal, normalize(vec3(-0.5, 0.55, 0.68))), 0.0);
  vec3 pearl = mix(mix(uBright, uTint, 0.35), mix(uPearl, vec3(1.0), 0.6), 0.55 + 0.45 * lit);

  // The film shows most where the glass is seen edge on, and a little everywhere.
  float fresnel = pow(1.0 - z, 1.6);
  float amount = clamp(0.16 + 0.84 * fresnel * (0.7 + 0.6 * thick), 0.0, 1.0);
  vec3 col = mix(pearl, filmColour, amount * mix(0.85, 1.0, uOnDark));

  // One soft seam where the film is thinnest: faint over the middle, clearer towards the edge.
  float seam = smoothstep(0.1, 0.0, abs(fract(phase + 0.35) - 0.5) - 0.4);
  col = mix(col, mix(uDeep, uShade, 0.5), seam * (0.02 + 0.26 * fresnel));

  // A soft window of light up and to the left, curved with the surface.
  vec2 pane = (q - vec2(-0.34, 0.38)) * vec2(1.0, 1.55);
  col = mix(col, vec3(1.0), exp(-dot(pane, pane) * 9.0) * 0.55);

  // The radar's sweep of light, turning clockwise through the glass as the funnel's does: soft at
  // its leading edge, fading behind it and towards the middle.
  float lead = -uSwirl * 1.6;
  float behind = mod(a - lead, TAU);
  float sweepLight =
    smoothstep(0.0, 0.35, behind) * exp(-behind * 1.9) * uSweep * smoothstep(0.2, 0.95, ql);
  col = mix(col, mix(uAccent, uTint, 0.35), clamp(sweepLight * 0.5, 0.0, 1.0));

  // Streaks of light hugging the rim on the lit side, then a crisp edge.
  float streaks = pow(max(0.0, cos(a - 2.3)), 9.0) + 0.6 * pow(max(0.0, cos(a + 2.45)), 16.0);
  float hug = smoothstep(0.8, 0.94, ql) * (1.0 - smoothstep(0.975, 1.0, ql));
  col = mix(col, vec3(1.0), clamp(streaks * hug, 0.0, 0.9));
  float edge = smoothstep(0.955, 0.992, ql) * (0.45 + 0.55 * thick);
  col = mix(col, mix(uDeep, uPearl, uOnDark), edge * 0.5);

  float grey = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(col, mix(vec3(grey), uGrey, 0.35), uDim * 0.8);
  float bodyAlpha = inside * 0.96 * (1.0 - 0.4 * uDim);

  float outside = max(len - rim, 0.0);
  float glow = exp(-outside * 8.0) * uGlow * 0.15 * (1.0 - smoothstep(0.8, 1.0, len));
  vec3 glowColor = mix(uAccent, uGrey, uDim);

  vec3 rgb = col * bodyAlpha + glowColor * glow * (1.0 - bodyAlpha);
  float alpha = bodyAlpha + glow * (1.0 - bodyAlpha);
  gl_FragColor = vec4(rgb, alpha);
}
`;

export interface OrbGl {
  /** Paint a frame on a canvas `side` device pixels square. */
  render(frame: OrbFrame, palette: OrbPalette, side: number): void;
  dispose(): void;
}

const UNIFORMS = [
  'uSide',
  'uScale',
  'uWobble',
  'uTime',
  'uFlow',
  'uSwirl',
  'uGlow',
  'uLight',
  'uSweep',
  'uDim',
  'uOnDark',
  'uAccent',
  'uBright',
  'uDeep',
  'uTint',
  'uPearl',
  'uShade',
  'uGrey',
  'uImpulses',
] as const;

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

type Rgb = [number, number, number];

const channels = (color: string): Rgb => {
  const rgb = rgbOf(color) ?? [30, 61, 51];
  return [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255];
};

/** The shader's colours for a palette: the accent's hues (`orbHues`) and the theme's grey. */
function coloursFor(palette: OrbPalette) {
  const dark = onDarkSurface(palette);
  const hues = orbHues(palette, dark);
  return {
    onDark: dark ? 1 : 0,
    accent: channels(hues.accent),
    bright: channels(hues.bright),
    deep: channels(hues.deep),
    tint: channels(hues.tint),
    pearl: channels(hues.pearl),
    shade: channels(hues.shade),
    grey: channels(palette.line),
  };
}

/**
 * The WebGL bubble on `canvas`, or null where WebGL or the shader is refused — once a canvas has
 * been asked for WebGL it cannot give a 2D context, so the caller falls back on a new one.
 * `onLost` fires if the browser takes the context away later.
 */
export function createOrbGl(canvas: HTMLCanvasElement, onLost?: () => void): OrbGl | null {
  let gl: WebGLRenderingContext | null = null;
  try {
    gl = canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: true,
      antialias: false,
      // Kept so a frame can be read back: the phone check counts the orb's drawn pixels.
      preserveDrawingBuffer: true,
    });
  } catch {
    gl = null;
  }
  if (!gl) return null;
  const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX);
  const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
  const program = gl.createProgram();
  if (!vertex || !fragment || !program) return null;
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);

  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, 'aPos');
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

  const context = gl;
  const at = Object.fromEntries(
    UNIFORMS.map((name) => [name, context.getUniformLocation(program, name)]),
  ) as Record<(typeof UNIFORMS)[number], WebGLUniformLocation | null>;
  const impulses = new Float32Array(ORB_IMPULSES * 3);
  let colours: { palette: OrbPalette; values: ReturnType<typeof coloursFor> } | null = null;

  const lost = (event: Event) => {
    event.preventDefault();
    onLost?.();
  };
  canvas.addEventListener('webglcontextlost', lost);

  return {
    render(frame, palette, side) {
      if (!gl || gl.isContextLost()) return;
      if (colours?.palette !== palette) colours = { palette, values: coloursFor(palette) };
      const c = colours.values;
      gl.viewport(0, 0, side, side);
      gl.uniform1f(at.uSide, side);
      gl.uniform1f(at.uScale, frame.scale);
      gl.uniform1f(at.uWobble, frame.wobble);
      gl.uniform1f(at.uTime, frame.time);
      gl.uniform1f(at.uFlow, frame.flow);
      gl.uniform1f(at.uSwirl, frame.swirl);
      gl.uniform1f(at.uGlow, frame.glow);
      gl.uniform1f(at.uLight, frame.light);
      gl.uniform1f(at.uSweep, frame.sweep);
      gl.uniform1f(at.uDim, frame.dim);
      gl.uniform1f(at.uOnDark, c.onDark);
      gl.uniform3fv(at.uAccent, c.accent);
      gl.uniform3fv(at.uBright, c.bright);
      gl.uniform3fv(at.uDeep, c.deep);
      gl.uniform3fv(at.uTint, c.tint);
      gl.uniform3fv(at.uPearl, c.pearl);
      gl.uniform3fv(at.uShade, c.shade);
      gl.uniform3fv(at.uGrey, c.grey);
      impulses.fill(0);
      frame.impulses.slice(0, ORB_IMPULSES).forEach((impulse, i) => {
        impulses[i * 3] = impulse.angle;
        impulses[i * 3 + 1] = impulse.strength;
        impulses[i * 3 + 2] = impulse.width;
      });
      gl.uniform3fv(at.uImpulses, impulses);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    },
    dispose() {
      // The context is left to the canvas, not lost on purpose: React mounts an effect twice in
      // development, and a context lost on the first would be the one the second is handed.
      canvas.removeEventListener('webglcontextlost', lost);
      gl = null;
    },
  };
}
